import { IDocumentParameterInput } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { Component, ReactNode } from "react";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import "./document-parameter-input-panel.scss";

/**
 * One declared document input, edited in the right panel (KOA-17254).
 *
 * <b>Why this looks like the screen-input card.</b> A document input plays the same role one level up: a value the
 * caller supplies, declared by name and type, referenced by symbols elsewhere. Someone who has declared a screen
 * input already knows this interaction — collapsed row showing the name, expand to edit, REMOVE / UPDATE at the
 * foot. Inventing a second vocabulary for the same concept would make the tool feel like a different product.
 *
 * Authoring lives here and not in the canvas for the same reason source actions do: the canvas answers "which tag
 * gets which value", the right panel holds the things a tag's value can be built from.
 */

/**
 * The types a document input can carry, matching the screen-input list minus the ones a SQL parameter cannot be:
 * an image is not something a query takes.
 */
const TYPE_OPTIONS: KuikaSelectboxOption[] = [
  { value: "Boolean", label: "Boolean" },
  { value: "DateTime", label: "DateTime" },
  { value: "DateTimeOffset", label: "DateTimeOffset" },
  { value: "Decimal", label: "Decimal" },
  { value: "Double", label: "Double" },
  { value: "Guid", label: "Guid" },
  { value: "Int32", label: "Int32" },
  { value: "Int64", label: "Int64" },
  { value: "String", label: "String" }
];

export enum EDocumentParameterInputPanelStatus {
  new = 1,
  edit = 2
}

export interface IDocumentParameterInputPanelProps {
  input: IDocumentParameterInput;
  status: EDocumentParameterInputPanelStatus;
  /** Names already taken, so a duplicate is refused before it reaches the backend. */
  existingNames: string[];
  onSaved: (input: IDocumentParameterInput) => void;
  onRemoved: () => void;
}

export interface IDocumentParameterInputPanelState {
  isExpanded: boolean;
}

/** Identifier-shaped: the name is a wire key and the bound parameter name at the call site. */
const NAME_PATTERN: RegExp = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;

interface IDocumentParameterInputFormValues {
  name: string;
  typeName: string;
}

/** What `rc-collapse` hands the `expandIcon` renderer; only the open flag is read here. */
interface ICollapseExpandIconProps {
  isActive?: boolean;
}

/** One card, one section — a stable literal because the card holds exactly one input. */
const DOCUMENT_PARAMETER_INPUT_KEY: string = "document-parameter-input";

/**
 * A plain component, not a `PureSdComponent`: this card reads nothing from the screen-design context. Taking the
 * context anyway would tie a self-contained form to the designer's change-reason machinery for no benefit.
 */
class DocumentParameterInputPanel extends Component<
  IDocumentParameterInputPanelProps,
  IDocumentParameterInputPanelState
> {
  constructor(props: IDocumentParameterInputPanelProps) {
    super(props);
    // A freshly added input opens expanded — it has nothing to show collapsed.
    this.state = { isExpanded: props.status === EDocumentParameterInputPanelStatus.new };
  }

  private handleSave = (values: IDocumentParameterInputFormValues): void => {
    this.setState({ isExpanded: false });
    this.props.onSaved({
      name: values.name.trim(),
      typeName: values.typeName,
      // Cardinality is not authored: a source action parameter takes one value. A list-valued input would have no
      // meaning on the receiving side, so offering it would be a control that cannot be honoured.
      cardinality: this.props.input.cardinality ?? 1
    });
  };

  private toggle = (): void => {
    const isNamed: boolean = !!this.props.input.name;
    if (!this.state.isExpanded || isNamed) {
      this.setState({ isExpanded: !this.state.isExpanded });
    }
  };

  private activeKey = (): string[] => (this.state.isExpanded ? [DOCUMENT_PARAMETER_INPUT_KEY] : []);

  /**
   * The chevron the whole right-hand column uses: it points right when closed and is rotated a
   * quarter turn when open. antd positioned it absolutely, which is why it used to carry a
   * `right: 12` — the kit lays the header out as a row and places it with `iconPlacement`.
   */
  private renderExpandIcon = (panelProps: ICollapseExpandIconProps): ReactNode => (
    <KuikaSvg
      name="chevron-right"
      type="arrows"
      width={16}
      height={16}
      className={panelProps.isActive ? "kuika_rotate90deg" : "kuika_rotate0deg"}
    />
  );

  render() {
    const { input, status } = this.props;
    const isNew: boolean = status === EDocumentParameterInputPanelStatus.new;
    const headerText: string = isNew ? "New Document Input" : input.name;

    return (
      <KuikaCollapsePanel
        onChange={this.toggle}
        activeKey={this.activeKey()}
        iconPlacement="right"
        className="kuika-right-panel__collapse kuika-right-panel__collapse--screen-input kuika-document-input__collapse"
        expandIcon={this.renderExpandIcon}
        items={[
          {
            key: DOCUMENT_PARAMETER_INPUT_KEY,
            label: <KuikaTextMedium>{headerText}</KuikaTextMedium>,
            children: (
              <div className="kuika_styling_typography_wrapper kuika_screen-input__wrapper">
                <Form
                  name={`document-parameter-input-${input.name || "new"}`}
                  onFinish={this.handleSave}
                  initialValues={{ name: input.name, typeName: input.typeName || "String" }}
                >
                  <KuikaSmallMediumText>Input Name</KuikaSmallMediumText>
                  <Form.Item
                    name="name"
                    rules={[
                      { required: true, message: "A name is required!" },
                      {
                        validator: (_rule, value: string) => {
                          if (!value) return Promise.resolve();
                          if (!NAME_PATTERN.test(value.trim())) {
                            return Promise.reject(
                              new Error("Start with a letter or underscore, then letters, digits and underscores.")
                            );
                          }
                          // Ordinal comparison: the backend compares these ordinally too, so a case-insensitive check
                          // here would refuse names the backend accepts.
                          if (this.props.existingNames.includes(value.trim())) {
                            return Promise.reject(new Error("This name is already used."));
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <KuikaInput style={{ width: "100%" }} autoFocus data-testid="documentParameterInputName" />
                  </Form.Item>

                  <KuikaSmallMediumText>Type</KuikaSmallMediumText>
                  <div className="kuika_property-grid-column" style={{ padding: 0 }}>
                    <Form.Item name="typeName" rules={[{ required: true, message: "A type is required!" }]}>
                      <KuikaSelectbox placeholder="Select a type" searchable options={TYPE_OPTIONS} />
                    </Form.Item>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <KuikaButton type="text" size="sm" onClick={this.props.onRemoved}>
                      <KuikaSmallMediumText>{isNew ? "CANCEL" : "REMOVE"}</KuikaSmallMediumText>
                    </KuikaButton>
                    <KuikaButton type="secondary" size="sm" htmlType="submit">
                      <KuikaSmallMediumText>{isNew ? "CREATE" : "UPDATE"}</KuikaSmallMediumText>
                    </KuikaButton>
                  </div>
                </Form>
              </div>
            )
          }
        ]}
      />
    );
  }
}

export { DocumentParameterInputPanel };
